import { Injectable } from '@angular/core';
import { State, Action, Selector, StateContext } from '@ngxs/store';
import { catchError, map } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { NotificationService } from '../../../../../common/services/notification/notification.service';
import { ShipmentPlannerProfileItemBriefService } from './shipment-planner-profile-item-brief.service';
import { ShipmentPlannerProfileModel } from '../models';
import { NavigateUserService } from '../../../../services/navigate-user.service';
import {
  ShipmentPlannerProfileItemBriefClone,
  ShipmentPlannerProfileItemBriefDelete,
  ShipmentPlannerProfileItemBriefInitialState,
  ShipmentPlannerProfileItemBriefLoad,
  ShipmentPlannerProfileItemBriefSave,
  ShipmentPlannerProfileItemBriefAffectedUsersLoad,
} from './shipment-planner-profile-item-brief.actions';
import { ShipmentPlannerProfileItemService } from '../item/shipment-planner-profile-item.service';
import { CustomHttpErrorResponse, Error } from '../../../../../common';
import { MatSnackBarRef, SimpleSnackBar } from '@angular/material/snack-bar';

export interface ShipmentPlannerProfileItemBriefStateModel {
  profile: ShipmentPlannerProfileModel | null;
  loaded: boolean;
  affectedUsers: number | null;
  affectedUsersLoaded: boolean;
}

const initialState: ShipmentPlannerProfileItemBriefStateModel = {
  profile: null,
  loaded: false,
  affectedUsers: null,
  affectedUsersLoaded: false,
};

@State<ShipmentPlannerProfileItemBriefStateModel>({
  name: 'shipmentPlannerProfileItemBrief',
  defaults: initialState,
})
@Injectable()
export class ShipmentPlannerProfileItemBriefState {
  constructor(
    private shipmentPlannerProfileItemBriefService: ShipmentPlannerProfileItemBriefService,
    private shipmentPlannerProfileItemService: ShipmentPlannerProfileItemService,
    private notificationService: NotificationService,
    private navigateUserService: NavigateUserService,
  ) {}

  @Selector()
  static getState(state: ShipmentPlannerProfileItemBriefStateModel): ShipmentPlannerProfileItemBriefStateModel {
    return state;
  }

  @Selector()
  static getLoaded(state: ShipmentPlannerProfileItemBriefStateModel): boolean {
    return state.loaded;
  }

  @Selector()
  static getProfileId(state: ShipmentPlannerProfileItemBriefStateModel): number {
    return state.profile.id;
  }

  @Selector()
  static getProfile(state: ShipmentPlannerProfileItemBriefStateModel): ShipmentPlannerProfileModel {
    return state.profile;
  }

  @Selector()
  static getAffectedUsersLoaded(state: ShipmentPlannerProfileItemBriefStateModel): boolean {
    return state.affectedUsersLoaded;
  }

  @Selector()
  static getAffectedUsers(state: ShipmentPlannerProfileItemBriefStateModel): number {
    return state.affectedUsers;
  }

  @Action(ShipmentPlannerProfileItemBriefLoad)
  getShipmentPlannerProfileItemBrief(
    ctx: StateContext<ShipmentPlannerProfileItemBriefStateModel>,
    { payload }: ShipmentPlannerProfileItemBriefLoad,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    ctx.patchState({ loaded: false });

    return this.shipmentPlannerProfileItemService.getShipmentPlannerProfile(payload).pipe(
      map((profile: ShipmentPlannerProfileModel) => {
        ctx.patchState({ profile, loaded: true });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(ShipmentPlannerProfileItemBriefSave)
  saveShipmentPlannerProfileItemBrief(
    ctx: StateContext<ShipmentPlannerProfileItemBriefStateModel>,
    { payload }: ShipmentPlannerProfileItemBriefSave,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    ctx.patchState({ loaded: false });

    return this.shipmentPlannerProfileItemBriefService.saveShipmentPlannerProfile(payload).pipe(
      map((profile: ShipmentPlannerProfileModel) => {
        ctx.patchState({ profile, loaded: true });
        this.notificationService.success('api.shipmentProfile.success.update');
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(ShipmentPlannerProfileItemBriefClone)
  cloneShipmentPlannerProfileItemBrief(
    ctx: StateContext<ShipmentPlannerProfileItemBriefStateModel>,
    { payload }: ShipmentPlannerProfileItemBriefClone,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.shipmentPlannerProfileItemBriefService.cloneShipmentPlannerProfile(payload).pipe(
      map(() => {
        this.notificationService.success('api.shipmentProfile.success.create');
        this.navigateUserService.navigateUserToAbsolutePath('/dashboard/monitoring/shipment-profiles');
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(ShipmentPlannerProfileItemBriefDelete)
  deleteShipmentPlannerProfileItemBrief(
    ctx: StateContext<ShipmentPlannerProfileItemBriefStateModel>,
    { payload }: ShipmentPlannerProfileItemBriefDelete,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | Observable<void>> {
    return this.shipmentPlannerProfileItemBriefService.deleteShipmentPlannerProfile(payload.profileId).pipe(
      map(() => {
        payload.dialog.close();
        this.notificationService.success('api.shipmentProfile.success.delete');
        this.navigateUserService.navigateUserToAbsolutePath('/dashboard/monitoring/shipment-profiles');
        return ctx.dispatch(new ShipmentPlannerProfileItemBriefInitialState());
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(ShipmentPlannerProfileItemBriefAffectedUsersLoad)
  getShipmentProfileItemDefaultProfileAffectedUsers(
    ctx: StateContext<ShipmentPlannerProfileItemBriefStateModel>,
    { payload }: ShipmentPlannerProfileItemBriefAffectedUsersLoad,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    ctx.patchState({ affectedUsersLoaded: false });

    return this.shipmentPlannerProfileItemBriefService.getShipmentDefaultProfileAffectedUsers(payload).pipe(
      map((affectedUsers: number) => {
        ctx.patchState({ affectedUsers, affectedUsersLoaded: true });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(ShipmentPlannerProfileItemBriefInitialState)
  setInitialState(ctx: StateContext<ShipmentPlannerProfileItemBriefStateModel>): void {
    ctx.setState(initialState);
  }
}
